import React, { useState } from "react";
import { useTenant } from "@/contexts/TenantContext";
import { useAuth } from "@/contexts/AuthContext";
import { api, API_BASE, getToken } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImportWizard from "@/components/ImportWizard";
import DownloadLinkDialog from "@/components/DownloadLinkDialog";
import RestoreDialog from "@/components/RestoreDialog";
import { toast } from "sonner";
import {
    Upload,
    FileSpreadsheet,
    ShieldAlert,
    FileWarning,
    Sparkles,
    ListChecks,
    Download,
    Trash2,
    Database,
    FileArchive,
} from "lucide-react";

export default function ImportPage() {
    const { activeTenant } = useTenant();
    const { user } = useAuth();
    const [wizardOpen, setWizardOpen] = useState(false);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetText, setResetText] = useState("");
    const [resetBusy, setResetBusy] = useState(false);
    const [backupDone, setBackupDone] = useState(false);
    const [finalConfirm, setFinalConfirm] = useState(false);
    const [dlDialog, setDlDialog] = useState({ open: false, url: "", filename: "", expiresInSec: 600 });
    const [restoreOpen, setRestoreOpen] = useState(false);

    const isAdmin = user?.role === "admin";

    if (user?.role === "guest") {
        return (
            <div className="max-w-2xl mx-auto mt-20 text-center animate-fade-up">
                <ShieldAlert className="h-12 w-12 mx-auto text-yellow-400" />
                <h2 className="font-heading text-2xl mt-4">Keine Berechtigung</h2>
                <p className="text-muted-foreground mt-2">Gäste können keine Importe ausführen.</p>
            </div>
        );
    }

    const downloadBackup = async () => {
        try {
            // 1) Token vom Backend holen (gültig für /admin/backup-tenant-download)
            const t = await api.post("/admin/download-token", {
                purpose: "backup-tenant",
                tenant_id: activeTenant.tenant_id,
            });
            const token = t.data?.token;
            const ttl = t.data?.expires_in || 600;
            if (!token) throw new Error("Kein Download-Token erhalten");

            // 2) Download-URL bauen
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/backup-tenant-download`
                + `?token=${encodeURIComponent(token)}`
                + `&tenant_id=${encodeURIComponent(activeTenant.tenant_id)}`;

            // 3) Dialog mit Auto-`window.open` + sichtbarem Kopier-Fallback öffnen.
            //    Der Dialog versucht automatisch einen neuen Tab zu öffnen
            //    (umgeht Iframe-Sandbox-Block). Falls Pop-up blockiert ist,
            //    kann der Benutzer die URL kopieren und manuell öffnen.
            const ts = new Date().toISOString().replace(/[:T]/g, "-").slice(0, 19);
            const filename = `backup_${activeTenant.tenant_id}_${ts}.zip`;
            setDlDialog({ open: true, url, filename, expiresInSec: ttl });
            return true;
        } catch (e) {
            toast.error(`Backup fehlgeschlagen: ${e?.response?.data?.detail || e.message || "Unbekannter Fehler"}`);
            return false;
        }
    };

    const runReset = async () => {
        setResetBusy(true);
        try {
            const fd = new FormData();
            fd.append("tenant_id", activeTenant.tenant_id);
            fd.append("bestaetigung", resetText);
            const r = await api.post("/admin/reset-tenant", fd);
            toast.success(
                `Reset ausgeführt: ${r.data.geloeschte_titel} Titel, ${r.data.geloeschte_bewegungen} Bewegungen gelöscht`,
            );
            setResetOpen(false);
            setResetText("");
            setBackupDone(false);
            setFinalConfirm(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Reset fehlgeschlagen");
        } finally {
            setResetBusy(false);
        }
    };

    const downloadBackupForReset = async () => {
        const ok = await downloadBackup();
        if (ok) setBackupDone(true);
    };

    const openReset = () => {
        setResetText("");
        setBackupDone(false);
        setFinalConfirm(false);
        setResetOpen(true);
    };

    return (
        <div className="space-y-6 pb-12 animate-fade-up">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Datenmanagement</div>
                <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-bold mt-1">Import</h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Importieren Sie Bewegungsdaten via Excel-Datei für{" "}
                    <span className="text-tenant font-medium">{activeTenant?.name}</span>.
                </p>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
                <Card className="lg:col-span-2 p-7 bg-surface1 border-border">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        <FileSpreadsheet className="h-3.5 w-3.5" /> Excel-Import-Assistent
                    </div>
                    <h3 className="font-heading text-xl font-semibold mt-1">Bewegungsdaten importieren</h3>
                    <p className="text-sm text-muted-foreground mt-2">
                        Der Assistent führt Sie in 6 Schritten durch den Import: Zeitraum → Datei → Spalten-Zuordnung →
                        Vorschau → <span className="text-tenant font-medium">Dry-Run-Validierung</span> → Import.
                    </p>
                    <Button
                        onClick={() => setWizardOpen(true)}
                        className="mt-5 bg-tenant text-[color:var(--tenant-accent-foreground)] hover:opacity-90"
                        data-testid="page-import-start"
                    >
                        <Upload className="h-4 w-4 mr-2" /> Import starten
                    </Button>
                </Card>

                <Card className="p-6 bg-surface1 border-border">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        <ListChecks className="h-3.5 w-3.5" /> Voraussetzungen
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-muted-foreground list-disc list-inside">
                        <li>.xlsx Datei mit Spaltenüberschriften</li>
                        <li>Pflichtspalten: ISBN, Haupttitel</li>
                        <li>Empfohlen: Erlösgruppe, Lager Ö, Lager D, HK Ist, HK Kal, ET</li>
                        <li>Erlösgruppe „2: Ebooks" wird automatisch übersprungen</li>
                    </ul>
                </Card>
            </div>

            <Card className="p-6 bg-surface1 border-border">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <FileWarning className="h-3.5 w-3.5 text-yellow-400" /> Import-Verhalten
                </div>
                <ul className="mt-3 space-y-1.5 text-sm text-muted-foreground list-disc list-inside">
                    <li>Inaktiv-Markierung erfolgt <strong>nur innerhalb der importierten Verlagsbereiche</strong> (Erlösgruppen).</li>
                    <li>Bestehende Titel mit 0-Lager werden auf inaktiv gesetzt; Stammdaten bleiben erhalten.</li>
                    <li>Neue Titel mit 0-Lager werden nicht übernommen.</li>
                    <li>Bestehende Bewegung für denselben Zeitraum + ISBN wird beim Import überschrieben.</li>
                    <li>Positionen ohne HK-Werte landen automatisch im Fehlerprotokoll zur manuellen Nachbearbeitung.</li>
                </ul>
            </Card>

            {/* Sonderauflagen-Import — nur für Admin */}
            {isAdmin && (
                <SonderauflagenCard tenantId={activeTenant?.tenant_id} />
            )}

            {/* Backup & Reset – nur für Admin */}
            {isAdmin && (
                <Card className="p-6 bg-surface1 border-border" data-testid="admin-data-tools">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        <Database className="h-3.5 w-3.5" /> Daten-Werkzeuge (nur Administrator)
                    </div>
                    <h3 className="font-heading text-lg font-semibold mt-1">Backup & Reset</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Empfohlen vor dem ersten Echt-Import: Aktuelle Daten als JSON sichern oder vollständig
                        zurücksetzen, falls nur Testdaten vorhanden sind.
                    </p>
                    <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        <div className="p-4 border border-border rounded-md bg-surface2 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <div className="font-medium text-sm">Backup als ZIP</div>
                                    <div className="text-xs text-muted-foreground">
                                        Titel + Bewegungen + WB-Config + Sonder-WB + Snapshots
                                    </div>
                                </div>
                                <Button variant="outline" onClick={downloadBackup} data-testid="backup-btn">
                                    <Download className="h-4 w-4 mr-2" /> Backup erstellen
                                </Button>
                            </div>
                        </div>
                        <div className="p-4 border border-amber-500/30 rounded-md bg-amber-500/5 space-y-3">
                            <div className="flex items-center justify-between gap-2">
                                <div>
                                    <div className="font-medium text-sm">Aus Backup wiederherstellen</div>
                                    <div className="text-xs text-muted-foreground">
                                        Backup-ZIP hochladen → alle Daten dieses Mandanten ersetzen
                                    </div>
                                </div>
                                <Button
                                    variant="outline"
                                    onClick={() => setRestoreOpen(true)}
                                    data-testid="restore-btn"
                                    className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                                >
                                    <FileArchive className="h-4 w-4 mr-2" /> Restore
                                </Button>
                            </div>
                        </div>
                        <div className="flex items-center justify-between gap-2 p-4 border border-red-500/30 rounded-md bg-red-500/5">
                            <div>
                                <div className="font-medium text-sm">Alle Daten löschen</div>
                                <div className="text-xs text-muted-foreground">
                                    Titel + Bewegungen + Aktivitäten für {activeTenant?.name}
                                </div>
                            </div>
                            <Button
                                variant="outline"
                                onClick={openReset}
                                data-testid="reset-btn"
                                className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                            >
                                <Trash2 className="h-4 w-4 mr-2" /> Reset
                            </Button>
                        </div>
                    </div>
                </Card>
            )}

            <ImportWizard open={wizardOpen} onOpenChange={setWizardOpen} onImported={() => {}} />

            <DownloadLinkDialog
                open={dlDialog.open}
                onOpenChange={(o) => setDlDialog((d) => ({ ...d, open: o }))}
                url={dlDialog.url}
                title="Backup herunterladen"
                description="Der Download wird in einem neuen Tab gestartet. Falls Ihr Browser den neuen Tab blockiert, kopieren Sie die URL und öffnen Sie sie manuell in einem neuen Browser-Tab."
                filename={dlDialog.filename}
                expiresInSec={dlDialog.expiresInSec}
            />

            <RestoreDialog
                open={restoreOpen}
                onOpenChange={setRestoreOpen}
                activeTenant={activeTenant}
                onDone={() => {
                    // einfache Refresh-Logik: harter Reload, damit alle
                    // Seiten (Inventory, Snapshots etc.) frische Daten ziehen.
                    setTimeout(() => window.location.reload(), 800);
                }}
            />

            <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <ShieldAlert className="h-5 w-5 text-red-400" />
                            Alle Daten von {activeTenant?.name} löschen?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="space-y-4 text-sm">
                                <p>
                                    Diese Aktion löscht <strong>alle Titel, Bewegungsdaten und Aktivitäten</strong>{" "}
                                    von {activeTenant?.name}. Andere Verlage bleiben unberührt.
                                </p>

                                {/* Schritt 1: Backup-Abfrage */}
                                <div className="p-3 rounded-md border border-border bg-surface2">
                                    <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">
                                        1. Backup vorher herunterladen?
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={downloadBackupForReset}
                                            data-testid="reset-backup-yes"
                                            className={backupDone ? "border-emerald-500/50 text-emerald-400" : ""}
                                        >
                                            <Download className="h-4 w-4 mr-2" />
                                            {backupDone ? "Backup erstellt ✓" : "Ja, Backup laden"}
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setBackupDone(true)}
                                            data-testid="reset-backup-no"
                                            className={backupDone ? "" : "text-muted-foreground"}
                                        >
                                            Nein, ohne Backup fortfahren
                                        </Button>
                                    </div>
                                </div>

                                {/* Schritt 2: RESET-Eingabe */}
                                <div className={backupDone ? "" : "opacity-40 pointer-events-none"}>
                                    <Label className="text-xs uppercase tracking-widest text-muted-foreground">
                                        2. Zur Bestätigung „RESET" eingeben
                                    </Label>
                                    <Input
                                        value={resetText}
                                        onChange={(e) => {
                                            setResetText(e.target.value);
                                            setFinalConfirm(false);
                                        }}
                                        placeholder="RESET"
                                        className="mt-1"
                                        data-testid="reset-confirm-input"
                                        disabled={!backupDone}
                                    />
                                </div>

                                {finalConfirm && (
                                    <div className="p-3 rounded-md border border-red-500/40 bg-red-500/10 text-red-300">
                                        <div className="text-xs uppercase tracking-widest mb-1">
                                            3. Letzte Bestätigung
                                        </div>
                                        <div className="text-sm">
                                            Wirklich endgültig alle Daten von {activeTenant?.name} löschen?
                                            <br />
                                            <span className="text-xs text-red-200/80">
                                                Diese Aktion kann nicht rückgängig gemacht werden.
                                            </span>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel disabled={resetBusy}>Abbrechen</AlertDialogCancel>
                        {!finalConfirm ? (
                            <Button
                                onClick={() => setFinalConfirm(true)}
                                disabled={
                                    resetBusy ||
                                    !backupDone ||
                                    resetText.trim().toUpperCase() !== "RESET"
                                }
                                className="bg-red-500 hover:bg-red-600 text-white"
                                data-testid="reset-prepare-btn"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Weiter
                            </Button>
                        ) : (
                            <AlertDialogAction
                                onClick={runReset}
                                disabled={resetBusy}
                                className="bg-red-600 hover:bg-red-700"
                                data-testid="reset-confirm-btn"
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                {resetBusy ? "Lösche…" : "Ja, endgültig löschen"}
                            </AlertDialogAction>
                        )}
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Sonderauflagen-Import-Karte
// ---------------------------------------------------------------------------
function SonderauflagenCard({ tenantId }) {
    const [file, setFile] = React.useState(null);
    const [jahr, setJahr] = React.useState("2024");
    const [busy, setBusy] = React.useState(false);
    const [result, setResult] = React.useState(null);

    const submit = async () => {
        if (!file || !tenantId) return;
        setBusy(true);
        setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const r = await api.post(
                `/import/sonderauflagen?tenant_id=${tenantId}&zeitraum=${jahr}`,
                fd,
                { headers: { "Content-Type": "multipart/form-data" } },
            );
            setResult(r.data);
            toast.success(r.data.message);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Import fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const downloadTemplate = async () => {
        try {
            const r = await api.get(`/import/sonderauflagen/template.xlsx`, { responseType: "blob" });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = "sonderauflagen_template.xlsx";
            a.click();
        } catch {
            toast.error("Vorlage konnte nicht geladen werden");
        }
    };

    return (
        <Card className="p-6 bg-surface1 border-border" data-testid="sonderauflagen-card">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5 text-tenant" /> Sonderauflagen-Import
            </div>
            <h3 className="font-heading text-xl font-semibold mt-1">Sonderauflagen einspielen (S-Tag)</h3>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                Für Aktions- oder Sonderausgaben, die nicht in der normalen Import-Excel enthalten sind
                (z. B. ALDI/Hofer-Editionen). Legt Titel und Bewegung für das gewählte Jahr an und markiert
                sie mit dem <strong className="text-foreground">S-Tag</strong>. Bewertung erfolgt nach normaler WB-Staffel.
            </p>
            <div className="mt-4 grid sm:grid-cols-[auto,1fr,auto,auto] items-center gap-3">
                <Tabs value={jahr} onValueChange={setJahr}>
                    <TabsList>
                        {["2023", "2024", "2025", "2026"].map((y) => <TabsTrigger key={y} value={y}>{y}</TabsTrigger>)}
                    </TabsList>
                </Tabs>
                <input
                    type="file"
                    accept=".xlsx"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="text-xs text-muted-foreground file:mr-3 file:px-3 file:py-2 file:rounded-md file:border file:border-border file:bg-surface2 file:text-foreground file:cursor-pointer"
                    data-testid="sonderauflagen-file"
                />
                <Button variant="ghost" size="sm" onClick={downloadTemplate} data-testid="sonderauflagen-template">
                    <Download className="h-3.5 w-3.5 mr-1" /> Vorlage
                </Button>
                <Button
                    onClick={submit}
                    disabled={busy || !file}
                    className="bg-tenant text-[color:var(--tenant-accent-foreground)]"
                    data-testid="sonderauflagen-import-btn"
                >
                    {busy ? "Importiere…" : "Importieren"}
                </Button>
            </div>
            {result && (
                <div className="mt-4 text-sm rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-100 px-3 py-2">
                    <strong>{result.imported}</strong> Sonderauflagen importiert für <strong>{jahr}</strong>.
                    {result.skipped > 0 && <> {result.skipped} Zeilen übersprungen.</>}
                </div>
            )}
        </Card>
    );
}
